import React, { useLayoutEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ModalCloseBtn, theme, useResponsive } from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import {
  LoadingAnimation,
  SignedOutView,
  TransparentModal,
} from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { useHeaderRight } from '../../components/Header/Header.hooks';
import { useRateOrderNavigation } from './RateOrder.navigation';
import { useRateOrder } from './RateOrderScreen.data';
import { LandingContent } from './RateOrderScreen.landing';
import { RatingContent } from './RateOrderScreen.rating';

// ─────────────────────────────────────────────────────────────────────────────

export const RateOrderScreen = () => {
  const { dismissRateOrder } = useRateOrderNavigation();
  const isLoggedIn = useIsLoggedIn();

  const { minWidth, match } = useResponsive();
  const { t } = useLocalizationContext();

  // ─── Navigation Helpers ──────────────────────────────────────────────

  const navigation = useNavigation();

  const navigationHeaderRightItems = useMemo<NavigationHeaderRightItems>(
    () => [
      {
        key: 'account.gift-code.header.close-btn',
        icon: 'IconClose',
        accessibilityLabel: t('general.close'),
        onPress: dismissRateOrder,
      },
    ],
    [dismissRateOrder, t],
  );
  const navigationHeaderRight = useHeaderRight({
    items: navigationHeaderRightItems,
  });

  useLayoutEffect(() => {
    navigation.setOptions({ headerRight: navigationHeaderRight });
  }, [navigation, navigationHeaderRight]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      size={match(['full', 'medium'])}
      onClose={dismissRateOrder}
    >
      {minWidth.tablet ? <ModalCloseBtn onPress={dismissRateOrder} /> : null}

      {isLoggedIn ? (
        <RateOrderScreenSignedInContent />
      ) : (
        <RateOrderScreenSignedOutContent />
      )}
    </TransparentModal>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const RateOrderScreenSignedOutContent = () => {
  return <SignedOutView redirectTo="orders" withoutFooter />;
};

const RateOrderScreenSignedInContent = () => {
  const {
    loading,
    submitting,
    landingTitle,
    landingSubtitle,
    ratingTitle,
    ratingSubtitle,
    ratingHint,
    ratingCta,
    cards,
    rating,
    handleHoverRating,
    handleRating,
    handleCardSelection,
    handleSubmit,
  } = useRateOrder();

  // ─────────────────────────────────────────────

  if (loading)
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation />
      </View>
    );

  return (
    <>
      {rating === 0 && landingTitle ? (
        <LandingContent
          ratingTitle={ratingTitle}
          landingTitle={landingTitle}
          landingSubtitle={landingSubtitle}
          rating={rating}
          onHoverRating={handleHoverRating}
          onRating={handleRating}
        />
      ) : null}

      {rating > 0 && ratingSubtitle ? (
        <RatingContent
          ratingTitle={ratingTitle}
          ratingSubtitle={ratingSubtitle}
          ratingHint={ratingHint}
          ratingCta={ratingCta}
          cards={cards}
          rating={rating}
          submitting={submitting}
          onHoverRating={handleHoverRating}
          onRating={handleRating}
          onSelectCard={handleCardSelection}
          onSubmit={handleSubmit}
        />
      ) : null}
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    paddingTop: theme.spacing.xlarge,
    paddingBottom: theme.spacing.xxlarge,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type NavigationHeaderRightItems = Parameters<typeof useHeaderRight>[0]['items'];
